<template>
  <div class="filters-container">
    <div v-if="appliedFilters && appliedFilters.length > 0" id="applied-filters" class="filter-row">
      <v-chip
        v-for="(f, index) in appliedFilters"
        :key="index"
        :disabled="isReset(f)"
        dark
        class="filters-chip elevation-2 mr-3 my-2"
        close
        close-icon="mdi-close"
        :title="$t(`device.filter.ids.${Object.keys(f)[0]}`)"
        @click:close="removeFilter(f)"
      >
        <span v-if="Object.keys(f)[0] === 'last_seen_seconds'" :id="`applied-filter-${Object.keys(f)[0]}`">{{ displayDate(Object.values(f)[0]) }}</span>
        <span v-else-if="Object.keys(f)[0] === 'variant_ids'" id="applied-filter-variants">{{ displayVariant(Object.values(f)[0]) }}</span>
        <span v-else :id="`applied-filter-${Object.keys(f)[0]}`">{{ Object.values(f)[0].join(", ") }}</span>
      </v-chip>
      <v-chip
        dark
        class="filters-reset elevation-2 mr-3 my-2"
        @click="resetFilters"
      >
        <img :src="require('@/assets/icons/filters/clear.svg')" alt="clear_filters">
        <span id="reset-filters">{{ $t('device.filter.reset_filters') }}</span>
      </v-chip>
      <AddBookmarkDialog :filters="appliedFilters" />
    </div>
    <div id="main-filters-container" class="filter-row">
      <FilterBookmarks @applyBookmark="applyBookmark" />
      <v-text-field
        id="filter-plate-id"
        v-model="multiSearch"
        :label="$t('device.filter.search_id_plate')"
        single-line
        outlined
        dark
        dense
        hide-details
        :color="colors.secondary"
        class="search-input"
        @keyup.enter.native="applyFilters"
      >
        <v-icon slot="append">mdi-magnify</v-icon>
      </v-text-field>
      <FilterItem
        filterid="device_types"
        translatekey="device_type"
        :suggestions="getSuggestions ? getSuggestions.suggestions : {}"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @sendFilter="updateFilters"
      />
      <FilterItem
        filterid="last_seen_seconds"
        translatekey="last_seen_seconds"
        :suggestions="{}"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @sendFilter="updateFilters"
      />
      <v-btn
        id="toggle-advanced-filters"
        :title="$t('device.filter.more_filters')"
        dark
        text
        class="mr-2 px-2"
        @click="toggleMoreFilters"
      >
        <v-icon dark>mdi-tune</v-icon>
        <span class="mx-1 device-list-toolbar-btn">
          {{ displayMoreFilters ? $t('device.filter.less_filters') : $t('device.filter.more_filters') }}
        </span>
        <v-icon v-if="displayMoreFilters" dark>mdi-menu-up</v-icon>
        <v-icon v-else dark>mdi-menu-down</v-icon>
      </v-btn>
      <v-btn
        id="apply-filters"
        :title="$t('common.action.apply')"
        class="device-filters-btn ctp-btn mr-4 px-8"
        dark
        rounded
        @click="applyFilters"
      >
        <div class="apply_btn_content">
          <img :src="require('@/assets/icons/filters/apply.svg')" alt="apply_filters">
          <span>{{ $t('common.action.apply') }}</span>
        </div>
      </v-btn>
    </div>
    <div v-show="displayMoreFilters" id="advanced-filters-container" class="filter-row">
      <FilterItem
        filterid="dtco_ids"
        translatekey="dtco_ids"
        :suggestions="{}"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @sendFilter="updateFilters"
      />
      <FilterItem
        filterid="customers_tsp_ids"
        translatekey="end_customer"
        :suggestions="getSuggestions ? getSuggestions.suggestions : {}"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @sendFilter="updateFilters"
      />
      <FilterItem
        filterid="device_status"
        translatekey="device_status"
        :suggestions="getSuggestions ? getSuggestions.suggestions : {}"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @sendFilter="updateFilters"
      />
      <FilterItem
        filterid="variant_ids"
        translatekey="variants"
        :suggestions="{ variant_ids: getVariants }"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @sendFilter="updateFilters"
      />
      <FilterItem
        filterid="tollzone_codes"
        translatekey="tollzone_codes"
        :suggestions="getSuggestions ? getSuggestions.suggestions : {}"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @sendFilter="updateFilters"
      />
      <FilterItem
        filterid="tollzone_names"
        translatekey="tollzone_names"
        :suggestions="getSuggestions ? getSuggestions.suggestions : {}"
        :reset="reset"
        :resetMe="resetMe"
        :appliedFilters="appliedFilters"
        @sendFilter="updateFilters"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, onMounted, ref } from 'vue';
import useStore from '@/utils/store/common';
import { INavigation } from '@/store/modules/devices/types';
import { Suggestions } from 'ctp-search-device';
import moment from 'moment';
import { colors } from '@/constants/theme/colors';
import { FilterGroup } from 'ctp-filter-group-management';
import FilterBookmarks from './components/FilterBookmarks.vue';
import AddBookmarkDialog from './components/AddBookmarkDialog.vue';
import FilterItem from './components/FilterItem.vue';
import './styles/list-filters.scss';

const store = useStore();

const getNavigationConfiguration: ComputedRef<INavigation> = computed(() => store.getters['devices/getNavigationConfiguration']);
const getSuggestions: ComputedRef<Suggestions> = computed(() => store.getters['devices/getSuggestions']);
const getVariants: ComputedRef<Suggestions> = computed(() => store.getters['tollDomain/getVariants']);

const displayDate = (seconds: number) => moment().subtract(seconds, 'seconds').format('DD/MM/YYYY');
const displayVariant = (ids: number[]) => {
  const variants = ids.map(variant => store.getters['tollDomain/getVariantById'](variant)?.name);
  return variants.join(', ');
};

const displayMoreFilters = ref(false);
const multiSearch = ref('');
const activeFiltersCount = ref(0);
const selectedFilters = ref<{ id: string, values: string[] }[]>([]);
const appliedFilters = ref<{ [key: string]: any }[]>([]);
const resetedFilters = ref<string[]>([]);
const reset = ref(false);
const resetMe = ref('');

function applyFilters(fetchDevices = true) {
  // Save filters to store
  store.dispatch('devices/saveDeviceFilters', { selectedFilters: selectedFilters.value, multiSearch: multiSearch.value });
  const filters: {[key: string]: any} = {};
  resetMe.value = '';
  resetedFilters.value = [];
  // Push all selected filters
  if (selectedFilters.value.length > 0) {
    selectedFilters.value.forEach((item) => {
      filters[item.id] = item.values;
    });
  }
  // Push multisearch bar values
  if (multiSearch.value !== '') {
    activeFiltersCount.value = 1;
    filters.identifierMatchers = multiSearch.value.replace(/ /g, '').split(',');
  }
  const newNavigationConfiguration = {
    ...getNavigationConfiguration
  };
  newNavigationConfiguration.value.filters = filters;
  newNavigationConfiguration.value.start = 0;
  // Fix bug 12427: chips stay opened even with no value inside
  appliedFilters.value = Object.entries(filters)
    .filter(([_, value]) => {
      if (value == null) return false;
      if (Array.isArray(value) && value.length === 0) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      return true;
    })
    .map(([key, value]) => ({ [key]: value }));
  if (fetchDevices) store.dispatch('devices/updateNavigation', { ...newNavigationConfiguration.value });
}

onMounted(() => {
  // Load existing filters, existing filters are applied at devicelist level DevicesList -> LoadList
  const savedFilters = sessionStorage.getItem('savedFilters');
  if (!savedFilters) return;
  selectedFilters.value = JSON.parse(savedFilters).selectedFilters;
  multiSearch.value = JSON.parse(savedFilters).multiSearch;
  applyFilters(false);
});

function toggleMoreFilters() {
  displayMoreFilters.value = !displayMoreFilters.value;
}

function updateFilters(key: string, val: string[]) {
  const index = selectedFilters.value.findIndex(item => item.id === key);
  if (index !== -1) {
    selectedFilters.value[index].values = val;
  } else {
    selectedFilters.value.push({ id: key, values: val });
  }
}

function resetFilterValues() {
  selectedFilters.value = [];
  multiSearch.value = '';
  activeFiltersCount.value = 0;
  appliedFilters.value = [];
  resetedFilters.value = [];
  reset.value = true;
  sessionStorage.removeItem('savedFilters');
}

function resetFilters() {
  resetFilterValues();
  const newNavigationConfiguration = {
    ...getNavigationConfiguration
  };
  newNavigationConfiguration.value.filters = {};
  newNavigationConfiguration.value.start = 0;
  store.dispatch('devices/updateNavigation', { ...newNavigationConfiguration.value });
}

const triggerReset = (filterID: string) => {
  resetMe.value = filterID;
  resetedFilters.value.push(filterID);
};

function removeFilter(f: any) {
  if (f.identifierMatchers) {
    multiSearch.value = '';
  } else {
    selectedFilters.value = selectedFilters.value.filter(item => item.id !== Object.keys(f)[0]); // enlève les chip
  }
  triggerReset(Object.keys(f)[0]);
}

function applyBookmark(bookmark: FilterGroup) {
  resetFilterValues();
  Object.entries(JSON.parse(bookmark.filters)).forEach(([key, values]: any) => {
    if (key === 'device_ids') {
      multiSearch.value = values.join(',');
    } else {
      selectedFilters.value.push({ id: key, values });
    }
  });
  applyFilters();
}

const isReset = computed(() => (f: any) => resetedFilters.value.includes(Object.keys(f)[0]));
</script>
